import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Button, message, Tag, Descriptions, Modal, Form, Input, Switch, Tabs } from 'antd';
import { ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined, LockOutlined, GoogleOutlined } from '@ant-design/icons';
import { systemAPI } from '../services/api';

const { TextArea } = Input;
const { TabPane } = Tabs;

const System = () => {
  const [systemStatus, setSystemStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [googlebotModalVisible, setGooglebotModalVisible] = useState(false);
  const [googlebotConfig, setGooglebotConfig] = useState(null);
  const [passwordForm] = Form.useForm();
  const [googlebotForm] = Form.useForm();

  useEffect(() => {
    fetchSystemStatus();
    fetchGooglebotConfig();
  }, []);

  const fetchSystemStatus = async () => {
    try {
      const response = await systemAPI.getStatus();
      setSystemStatus(response.data);
    } catch (error) {
      message.error('获取系统状态失败');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    return status ? 'green' : 'red';
  };

  const getStatusIcon = (status) => {
    return status ? <CheckCircleOutlined /> : <CloseCircleOutlined />;
  };

  const getStatusText = (status) => {
    return status ? '正常' : '异常';
  };

  const fetchGooglebotConfig = async () => {
    try {
      const response = await systemAPI.getGooglebotConfig();
      setGooglebotConfig(response.data);
      googlebotForm.setFieldsValue(response.data);
    } catch (error) {
      message.error('获取Googlebot配置失败');
    }
  };

  const handleChangePassword = async (values) => {
    try {
      await systemAPI.changePassword(values);
      message.success('密码修改成功，请重新登录');
      setPasswordModalVisible(false);
      passwordForm.resetFields();
      // 清除登录信息，跳转到登录页
      setTimeout(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/';
      }, 1500);
    } catch (error) {
      message.error(error.response?.data?.error || '密码修改失败');
    }
  };

  const handleUpdateGooglebotConfig = async (values) => {
    try {
      await systemAPI.updateGooglebotConfig(values);
      message.success('Googlebot配置更新成功');
      setGooglebotModalVisible(false);
      fetchGooglebotConfig();
    } catch (error) {
      message.error('Googlebot配置更新失败');
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', gap: '8px' }}>
        <Button
          type="primary"
          icon={<ReloadOutlined />}
          onClick={fetchSystemStatus}
          loading={loading}
        >
          刷新状态
        </Button>
        <Button
          icon={<LockOutlined />}
          onClick={() => setPasswordModalVisible(true)}
        >
          修改密码
        </Button>
        <Button
          icon={<GoogleOutlined />}
          onClick={() => setGooglebotModalVisible(true)}
        >
          Googlebot配置
        </Button>
      </div>

      {systemStatus && (
        <>
          {/* 系统状态卡片 */}
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Redis状态"
                  value={getStatusText(systemStatus.redis)}
                  valueStyle={{ color: getStatusColor(systemStatus.redis) }}
                  prefix={getStatusIcon(systemStatus.redis)}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="数据库状态"
                  value={getStatusText(systemStatus.database)}
                  valueStyle={{ color: getStatusColor(systemStatus.database) }}
                  prefix={getStatusIcon(systemStatus.database)}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="系统版本"
                  value={systemStatus.version}
                />
              </Card>
            </Col>
          </Row>

          {/* 详细状态信息 */}
          <Card title="系统详细信息" style={{ marginBottom: 24 }}>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="时间戳">
                {new Date(systemStatus.timestamp * 1000).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="系统版本">
                {systemStatus.version}
              </Descriptions.Item>
              <Descriptions.Item label="Redis连接">
                <Tag color={getStatusColor(systemStatus.redis)}>
                  {getStatusText(systemStatus.redis)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="数据库连接">
                <Tag color={getStatusColor(systemStatus.database)}>
                  {getStatusText(systemStatus.database)}
                </Tag>
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* 性能监控提示 */}
          <Card title="系统说明">
            <div>
              <h4>架构说明：</h4>
              <ul>
                <li><strong>后端：</strong>Go + Gin框架，支持高并发处理</li>
                <li><strong>数据库：</strong>PostgreSQL (配置/统计) + Redis (缓存/去重)</li>
                <li><strong>前端：</strong>React + Ant Design</li>
                <li><strong>部署：</strong>Docker + Kubernetes (支持水平扩展)</li>
              </ul>

              <h4>性能特点：</h4>
              <ul>
                <li>单机可处理10万+QPS</li>
                <li>Redis缓存12小时去重</li>
                <li>数据库分区表优化查询</li>
                <li>异步统计不阻塞主流程</li>
              </ul>

              <h4>监控指标：</h4>
              <ul>
                <li>Prometheus + Grafana 监控系统</li>
                <li>实时请求数、分发率统计</li>
                <li>Web3钱包扩展检测数据</li>
                <li>域名和脚本使用统计</li>
              </ul>
            </div>
          </Card>
        </>
      )}

      {/* 修改密码弹窗 */}
      <Modal
        title="修改管理员密码"
        open={passwordModalVisible}
        onOk={() => passwordForm.submit()}
        onCancel={() => {
          setPasswordModalVisible(false);
          passwordForm.resetFields();
        }}
        okText="确认修改"
        cancelText="取消"
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handleChangePassword}
        >
          <Form.Item
            label="旧密码"
            name="old_password"
            rules={[{ required: true, message: '请输入旧密码' }]}
          >
            <Input.Password placeholder="请输入旧密码" />
          </Form.Item>
          <Form.Item
            label="新密码"
            name="new_password"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码至少6位' }
            ]}
          >
            <Input.Password placeholder="请输入新密码（至少6位）" />
          </Form.Item>
          <Form.Item
            label="确认新密码"
            name="confirm_password"
            dependencies={['new_password']}
            rules={[
              { required: true, message: '请确认新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('new_password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="请再次输入新密码" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Googlebot配置弹窗 */}
      <Modal
        title="Googlebot 自定义响应配置"
        open={googlebotModalVisible}
        onOk={() => googlebotForm.submit()}
        onCancel={() => {
          setGooglebotModalVisible(false);
          googlebotForm.resetFields();
        }}
        width={800}
        okText="保存配置"
        cancelText="取消"
      >
        <Form
          form={googlebotForm}
          layout="vertical"
          onFinish={handleUpdateGooglebotConfig}
          initialValues={{
            is_enabled: false,
            html_content: '<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Welcome</title></head><body><h1>Welcome to our site</h1><p>This is SEO-friendly content for search engines.</p></body></html>'
          }}
        >
          <Form.Item
            label="启用自定义响应"
            name="is_enabled"
            valuePropName="checked"
          >
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>
          <Form.Item
            label="HTML内容"
            name="html_content"
            rules={[{ required: true, message: '请输入HTML内容' }]}
            extra="当Googlebot访问时，将返回此HTML内容（用于SEO优化）"
          >
            <TextArea 
              rows={12} 
              placeholder="请输入HTML内容" 
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default System;
